/**
 * PromptDiffModal
 *
 * Displays a unified text diff between two prompt versions.
 * Parses the unified diff string into added/removed/unchanged lines
 * with color-coded styling.
 */

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { ScrollArea } from '../../../../ui/scroll-area';
import { usePromptDiff } from '../../hooks/usePrompts';

interface PromptDiffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionAId: string;
  versionBId: string;
  versionALabel: string;
  versionBLabel: string;
}

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged' | 'header';
  content: string;
}

function parseUnifiedDiff(diff: string): DiffLine[] {
  const lines = diff.split('\n');
  const result: DiffLine[] = [];

  for (const line of lines) {
    if (line.startsWith('---') || line.startsWith('+++')) {
      // Skip file headers from unified diff
      continue;
    } else if (line.startsWith('@@')) {
      result.push({ type: 'header', content: line });
    } else if (line.startsWith('+')) {
      result.push({ type: 'added', content: line.slice(1) });
    } else if (line.startsWith('-')) {
      result.push({ type: 'removed', content: line.slice(1) });
    } else if (line.startsWith(' ')) {
      result.push({ type: 'unchanged', content: line.slice(1) });
    } else if (line === '') {
      result.push({ type: 'unchanged', content: '' });
    }
  }

  return result;
}

const LINE_STYLES: Record<DiffLine['type'], string> = {
  added: 'bg-green-50 text-green-800 border-l-2 border-green-400',
  removed: 'bg-red-50 text-red-800 border-l-2 border-red-400',
  unchanged: 'text-gray-700',
  header: 'bg-blue-50 text-blue-600 font-medium',
};

const PromptDiffModal: React.FC<PromptDiffModalProps> = ({
  open,
  onOpenChange,
  versionAId,
  versionBId,
  versionALabel,
  versionBLabel,
}) => {
  const { data, isLoading, isError, error } = usePromptDiff(versionAId, versionBId, open);

  const parsedLines = useMemo(() => {
    if (!data?.diff) return [];
    return parseUnifiedDiff(data.diff);
  }, [data?.diff]);

  const addedCount = parsedLines.filter((l) => l.type === 'added').length;
  const removedCount = parsedLines.filter((l) => l.type === 'removed').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            Diff: {versionALabel} → {versionBLabel}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-gray-500">Loading diff...</div>
        ) : isError ? (
          <div className="py-8 text-center">
            <p className="text-red-600 font-medium">Failed to load diff</p>
            <p className="text-sm text-gray-500 mt-1">{error?.message || 'Unknown error'}</p>
          </div>
        ) : parsedLines.length > 0 ? (
          <div className="space-y-3">
            <div className="flex gap-3 text-xs">
              <span className="text-green-700 font-medium">+{addedCount} added</span>
              <span className="text-red-700 font-medium">-{removedCount} removed</span>
            </div>

            <ScrollArea className="h-[50vh]">
              <pre className="text-xs font-mono whitespace-pre-wrap pr-4">
                {parsedLines.map((line, i) => (
                  <div
                    key={i}
                    className={`px-2 py-0.5 ${LINE_STYLES[line.type]}`}
                  >
                    {line.type === 'added' && '+ '}
                    {line.type === 'removed' && '- '}
                    {line.type === 'unchanged' && '  '}
                    {line.content || '\u00A0'}
                  </div>
                ))}
              </pre>
            </ScrollArea>
          </div>
        ) : data ? (
          <p className="text-center text-gray-500 py-8">No differences found.</p>
        ) : (
          <p className="text-center text-gray-500 py-8">Failed to load diff.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PromptDiffModal;
